import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import FlightTakeoffRoundedIcon from "@mui/icons-material/FlightTakeoffRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import { BadmintonMarkIcon } from "@bw/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { TournamentResponse } from "@bw/shared/types/generated";
import { formatTournamentDateRange } from "@bw/shared/utils";
import type { ReactNode } from "react";
import { OfficialRatingNote } from "./OfficialBadge";

const safeExternalHref = (value: string | null | undefined) => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
};

const formatMoney = (value: unknown, currency: string) => {
  const amount =
    typeof value === "string" || typeof value === "number"
      ? Number(value)
      : Number.NaN;
  return Number.isFinite(amount)
    ? new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(amount)
    : null;
};

const formatDateTime = (value: string, timezone: string) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  });

function OverviewCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
          <Typography component="h2" sx={{ fontWeight: 800 }} variant="h6">
            {title}
          </Typography>
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

export function TournamentOverviewPanel({
  tournament,
}: {
  tournament: TournamentResponse;
}) {
  const timezone = tournament.facility_timezone ?? "UTC";
  const scoring = tournament.scoring_system;
  const rules = tournament.match_rules;
  const equipment = tournament.official_equipment;
  const logistics = tournament.tournament_logistics;
  const commerce = tournament.registration_commerce;
  const lifecycle = tournament.lifecycle;
  const playWindows = tournament.play_windows ?? [];
  const sponsors = tournament.sponsors ?? [];
  const airports = logistics?.airports ?? [];
  const hotelBookingHref = safeExternalHref(logistics?.hotel?.booking_url);
  const currency = commerce?.currency ?? "USD";
  const refundWindows = (tournament.refund_tiers ?? []).flatMap((tier) =>
    typeof tier.days_before === "number" && typeof tier.percent === "number"
      ? [{ days: tier.days_before, percent: tier.percent }]
      : [],
  );
  const retainedFee = (tournament.refund_tiers ?? []).find(
    (tier) => tier.retained_fee !== undefined,
  )?.retained_fee;
  const registrationOpens = tournament.registration_open_at;
  const registrationCloses = tournament.registration_close_at;
  const showNextMilestone = Boolean(
    lifecycle?.next_milestone_at &&
      lifecycle.next_milestone_label &&
      lifecycle.next_milestone_at !== registrationCloses,
  );
  const requirements = [
    tournament.required_federation
      ? `Current ${tournament.required_federation} membership`
      : tournament.compliance_requirements.require_federation_membership
        ? "Current governing-organization membership"
        : null,
    tournament.compliance_requirements.require_ncsi_for_18plus
      ? "Adult participant background check"
      : null,
    tournament.compliance_requirements.require_foreign_country_declaration
      ? "International representation declaration"
      : null,
    tournament.coach_pass_required ? "Coach pass for coached entries" : null,
  ].filter((item): item is string => Boolean(item));
  const hasTravel = Boolean(
    tournament.venue_name ||
      tournament.venue_address ||
      logistics?.hotel?.name ||
      logistics?.hotel?.address ||
      hotelBookingHref ||
      logistics?.hotel?.room_block_code ||
      airports.length ||
      logistics?.parking?.notes ||
      logistics?.parking?.fee,
  );
  const hasRules = Boolean(
    scoring ||
      tournament.match_duration_minutes ||
      tournament.match_no_show_minutes ||
      rules?.doubles_attire_required_matching ||
      rules?.line_judge_duty_rule,
  );
  const hasEquipment = Boolean(
    equipment?.shuttlecock_brand ||
      equipment?.shuttlecock_model ||
      equipment?.shuttlecock_fallback ||
      equipment?.court_surface,
  );

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography component="h2" sx={{ fontWeight: 800 }} variant="h5">
          Tournament overview
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Key dates, playing conditions, entry requirements, and travel details
          published by the organizer.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
        }}
      >
        <OverviewCard
          title="Key dates"
          icon={<AccessTimeRoundedIcon aria-hidden fontSize="small" />}
        >
          <Typography sx={{ fontWeight: 700 }}>
            {formatTournamentDateRange(
              tournament.start_date,
              tournament.end_date,
            )}
          </Typography>
          <Stack spacing={0.5}>
            {registrationOpens ? (
              <Typography color="text.secondary" variant="body2">
                Entries open {formatDateTime(registrationOpens, timezone)}
              </Typography>
            ) : null}
            {registrationCloses ? (
              <Typography color="text.secondary" variant="body2">
                Entries close {formatDateTime(registrationCloses, timezone)}
              </Typography>
            ) : null}
            {tournament.refund_deadline_at ? (
              <Typography color="text.secondary" variant="body2">
                Refund deadline{" "}
                {formatDateTime(tournament.refund_deadline_at, timezone)}
              </Typography>
            ) : null}
            {showNextMilestone &&
            lifecycle?.next_milestone_at &&
            lifecycle.next_milestone_label ? (
              <Typography color="text.secondary" variant="body2">
                {lifecycle.next_milestone_label}:{" "}
                {formatDateTime(lifecycle.next_milestone_at, timezone)}
              </Typography>
            ) : null}
          </Stack>
          {playWindows.length ? (
            <Stack spacing={0.5} sx={{ pt: 0.75 }}>
              <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
                Published play windows
              </Typography>
              {playWindows.map((window) => (
                <Typography
                  color="text.secondary"
                  key={window.window_id}
                  variant="body2"
                >
                  {new Date(`${window.play_date}T00:00:00Z`).toLocaleDateString(
                    undefined,
                    { dateStyle: "medium", timeZone: "UTC" },
                  )}
                  : {window.opens_at_local}–{window.closes_at_local}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </OverviewCard>

        <OverviewCard
          title="Events and entry"
          icon={<EmojiEventsOutlinedIcon aria-hidden fontSize="small" />}
        >
          {tournament.is_official ? <OfficialRatingNote /> : null}
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", rowGap: 1 }}
          >
            <Chip
              label={`${commerce?.event_count ?? 0} event${commerce?.event_count === 1 ? "" : "s"}`}
              size="small"
              variant="outlined"
            />
            {commerce?.entry_fee_from !== null &&
            commerce?.entry_fee_from !== undefined &&
            commerce.currency ? (
              <Chip
                label={`From ${formatMoney(
                  commerce.entry_fee_from,
                  commerce.currency,
                )}`}
                size="small"
                variant="outlined"
              />
            ) : commerce?.free_event_count ? (
              <Chip
                label="Free entry available"
                size="small"
                variant="outlined"
              />
            ) : null}
          </Stack>
          {requirements.length ? (
            <Stack spacing={0.5}>
              <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
                Entry requirements
              </Typography>
              {requirements.map((requirement) => (
                <Typography
                  color="text.secondary"
                  key={requirement}
                  variant="body2"
                >
                  • {requirement}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary" variant="body2">
              No additional entry requirements have been published.
            </Typography>
          )}
        </OverviewCard>

        {tournament.pricing_tiers?.length ||
        refundWindows.length ||
        retainedFee !== undefined ||
        tournament.refund_deadline_at ? (
          <OverviewCard
            title="Pricing and refunds"
            icon={<EmojiEventsOutlinedIcon aria-hidden fontSize="small" />}
          >
            {tournament.pricing_tiers?.map((tier) => (
              <Typography key={tier.event_count}>
                {tier.event_count} {tier.event_count === 1 ? "event" : "events"}
                : {formatMoney(tier.fee, currency) ?? "Fee pending"}
              </Typography>
            ))}
            {refundWindows.map((window) => (
              <Typography
                color="text.secondary"
                key={`${window.days}-${window.percent}`}
                variant="body2"
              >
                {window.days}+ days before start: {window.percent}% refund
              </Typography>
            ))}
            {retainedFee !== undefined ? (
              <Typography color="text.secondary" variant="body2">
                Processing amount retained:{" "}
                {formatMoney(retainedFee, currency) ?? "See policy"}
              </Typography>
            ) : null}
          </OverviewCard>
        ) : null}

        {hasRules ? (
          <OverviewCard
            title="Format and match rules"
            icon={<GavelRoundedIcon aria-hidden fontSize="small" />}
          >
            {scoring ? (
              <Typography>
                Best of {scoring.games_per_match} games ·{" "}
                {scoring.points_per_game} points per game · cap at{" "}
                {scoring.setting_point + 1}
              </Typography>
            ) : null}
            <Stack spacing={0.5}>
              {scoring ? (
                <Typography color="text.secondary" variant="body2">
                  {scoring.type === "traditional"
                    ? "Traditional service scoring"
                    : "Rally scoring"}
                </Typography>
              ) : null}
              {tournament.match_duration_minutes ? (
                <Typography color="text.secondary" variant="body2">
                  Planned match block: {tournament.match_duration_minutes}{" "}
                  minutes
                </Typography>
              ) : null}
              {(tournament.match_no_show_minutes ??
              rules?.no_show_default_minutes) ? (
                <Typography color="text.secondary" variant="body2">
                  No-show window:{" "}
                  {tournament.match_no_show_minutes ??
                    rules?.no_show_default_minutes}{" "}
                  minutes
                </Typography>
              ) : null}
              {rules?.doubles_attire_required_matching ? (
                <Typography color="text.secondary" variant="body2">
                  Doubles partners must wear matching attire.
                </Typography>
              ) : null}
              {rules?.line_judge_duty_rule ? (
                <Typography color="text.secondary" variant="body2">
                  Line-judge duty: {rules.line_judge_duty_rule}
                </Typography>
              ) : null}
            </Stack>
          </OverviewCard>
        ) : null}

        {hasEquipment ? (
          <OverviewCard
            title="Official equipment"
            icon={<BadmintonMarkIcon aria-hidden fontSize="small" />}
          >
            {equipment?.shuttlecock_brand || equipment?.shuttlecock_model ? (
              <Typography>
                Shuttle:{" "}
                {[equipment.shuttlecock_brand, equipment.shuttlecock_model]
                  .filter(Boolean)
                  .join(" ")}
              </Typography>
            ) : null}
            {equipment?.shuttlecock_fallback ? (
              <Typography color="text.secondary" variant="body2">
                Backup shuttle: {equipment.shuttlecock_fallback}
              </Typography>
            ) : null}
            {equipment?.court_surface ? (
              <Typography color="text.secondary" variant="body2">
                Court surface: {equipment.court_surface}
              </Typography>
            ) : null}
          </OverviewCard>
        ) : null}

        {hasTravel ? (
          <OverviewCard
            title="Venue and travel"
            icon={<FlightTakeoffRoundedIcon aria-hidden fontSize="small" />}
          >
            {tournament.venue_name ? (
              <Typography sx={{ fontWeight: 700 }}>
                {tournament.venue_name}
              </Typography>
            ) : null}
            {tournament.venue_address ? (
              <Typography color="text.secondary" variant="body2">
                {tournament.venue_address}
              </Typography>
            ) : null}
            {airports.length ? (
              <Typography variant="body2">
                Airports:{" "}
                {airports
                  .map((airport) =>
                    airport.name
                      ? `${airport.code} (${airport.name})`
                      : airport.code,
                  )
                  .join(", ")}
              </Typography>
            ) : null}
            {logistics?.hotel?.name ? (
              <Typography variant="body2">
                Hotel: {logistics.hotel.name}
              </Typography>
            ) : null}
            {logistics?.hotel?.address ? (
              <Typography color="text.secondary" variant="body2">
                {logistics.hotel.address}
              </Typography>
            ) : null}
            {logistics?.hotel?.room_block_code ? (
              <Typography color="text.secondary" variant="body2">
                Room block: {logistics.hotel.room_block_code}
              </Typography>
            ) : null}
            {hotelBookingHref ? (
              <Link href={hotelBookingHref} rel="noreferrer" target="_blank">
                Book the tournament hotel
              </Link>
            ) : null}
            {logistics?.parking?.notes ? (
              <Typography variant="body2">
                Parking: {logistics.parking.notes}
              </Typography>
            ) : null}
            {logistics?.parking?.fee ? (
              <Typography color="text.secondary" variant="body2">
                Parking fee:{" "}
                {formatMoney(logistics.parking.fee, currency) ?? "See venue"}
              </Typography>
            ) : null}
          </OverviewCard>
        ) : null}

        {sponsors.length ? (
          <OverviewCard
            title="Tournament partners"
            icon={<EmojiEventsOutlinedIcon aria-hidden fontSize="small" />}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
              {sponsors.map((sponsor) => {
                const websiteHref = safeExternalHref(sponsor.website_url);
                return websiteHref ? (
                  <Chip
                    clickable
                    component="a"
                    href={websiteHref}
                    key={sponsor.sponsor_id}
                    label={sponsor.name}
                    rel="noreferrer"
                    target="_blank"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    key={sponsor.sponsor_id}
                    label={sponsor.name}
                    variant="outlined"
                  />
                );
              })}
            </Stack>
          </OverviewCard>
        ) : null}
      </Box>

      <Typography color="text.secondary" variant="caption">
        Times shown in {timezone}. Draws, court assignments, and match times may
        change; use Schedule for the latest published state.
      </Typography>
    </Stack>
  );
}
